import { useState } from "react";
import {
  Table,
  Badge,
  Button,
  Group,
  Text,
  Title,
  Card,
  Stack,
  Center,
  Loader,
  Tooltip,
  Modal,
  Textarea,
  Box,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconX, IconShieldHalf } from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";

type ReviewItem = {
  userId: string;
  invitee: string;
  inviteeEmail: string;
  referrerEmail: string;
  referrerQuality: number | null;
  riskScore: number | null;
  riskReasons: string[];
  flaggedAt: string | null;
  joinedAt: string | null;
};

export default function FraudReview() {
  const { data, isLoading, reloadData } = usePerformRequest<{
    data: ReviewItem[];
  }>({
    method: Endpoints.GetReferralReviewQueue().method,
    route: Endpoints.GetReferralReviewQueue().route,
  });

  const items: ReviewItem[] = Array.isArray(data?.data) ? data!.data : [];
  const [busyId, setBusyId] = useState<string | null>(null);
  const [denyId, setDenyId] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState("");

  const approve = async (userId: string) => {
    setBusyId(userId);
    try {
      const ep = Endpoints.ApproveReferral(userId);
      const res = await PerformRequest({ method: ep.method, route: ep.route });
      if (!res.status || res.status >= 400) {
        throw new Error((res as { message?: string }).message || "Failed");
      }
      notifications.show({
        title: "Approved",
        message: "Referral bonus approved and paid.",
        color: "teal",
      });
      await reloadData();
    } catch (e: unknown) {
      notifications.show({
        title: "Error",
        message: e instanceof Error ? e.message : "Failed",
        color: "red",
      });
    } finally {
      setBusyId(null);
    }
  };

  const deny = async () => {
    if (!denyId) return;
    setBusyId(denyId);
    try {
      const ep = Endpoints.DenyReferral(denyId);
      const res = await PerformRequest({
        method: ep.method,
        route: ep.route,
        data: { reason: denyReason.trim() },
      });
      if (!res.status || res.status >= 400) {
        throw new Error((res as { message?: string }).message || "Failed");
      }
      notifications.show({
        title: "Denied",
        message: "Referral denied — no payout.",
        color: "orange",
      });
      setDenyId(null);
      setDenyReason("");
      await reloadData();
    } catch (e: unknown) {
      notifications.show({
        title: "Error",
        message: e instanceof Error ? e.message : "Failed",
        color: "red",
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box p="md">
      <Group mb="xs" gap="xs">
        <IconShieldHalf size={24} />
        <Title order={3}>Fraud Review — Referral bonuses</Title>
      </Group>
      <Text c="dimmed" mb="lg" size="sm">
        Medium-risk referrals held for a human decision. <b>Approve</b> pays the
        ₦250 first half; <b>Deny</b> blocks the bonus. Strong signals (same bank /
        social) were auto-denied and never reach here.
      </Text>

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : items.length === 0 ? (
        <Card withBorder p="xl">
          <Center>
            <Text c="dimmed">Nothing awaiting review 🎉</Text>
          </Center>
        </Card>
      ) : (
        <Table.ScrollContainer minWidth={820}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Invitee</Table.Th>
                <Table.Th>Referrer</Table.Th>
                <Table.Th>Risk</Table.Th>
                <Table.Th>Why flagged</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((it) => (
                <Table.Tr key={it.userId}>
                  <Table.Td>
                    <Text fw={600}>{it.invitee}</Text>
                    <Text size="xs" c="dimmed">
                      {it.inviteeEmail}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{it.referrerEmail}</Text>
                    {it.referrerQuality != null && (
                      <Text size="xs" c="dimmed">
                        quality {it.referrerQuality}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={(it.riskScore ?? 0) >= 40 ? "red" : "yellow"}>
                      {it.riskScore ?? "—"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {(it.riskReasons || []).slice(0, 4).map((r, i) => (
                        <Badge key={i} size="xs" variant="light" color="gray">
                          {r}
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Tooltip label="Approve & pay ₦250">
                        <Button
                          size="xs"
                          color="teal"
                          leftSection={<IconCheck size={14} />}
                          loading={busyId === it.userId}
                          onClick={() => approve(it.userId)}
                        >
                          Approve
                        </Button>
                      </Tooltip>
                      <Tooltip label="Deny bonus">
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          leftSection={<IconX size={14} />}
                          onClick={() => {
                            setDenyId(it.userId);
                            setDenyReason("");
                          }}
                        >
                          Deny
                        </Button>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Modal
        opened={!!denyId}
        onClose={() => setDenyId(null)}
        title="Deny referral bonus"
        centered
      >
        <Stack>
          <Textarea
            label="Reason (optional)"
            placeholder="Why is this being denied?"
            value={denyReason}
            onChange={(e) => setDenyReason(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDenyId(null)}>
              Cancel
            </Button>
            <Button color="red" loading={busyId === denyId} onClick={deny}>
              Deny bonus
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
